/**
 * Capoeira theme with Brazilian color palette.
 *
 * Colors inspired by the Brazilian flag:
 * - Green: #006B2B (Bandeira verde)
 * - Blue: #002776 (Bandeira azul)
 * - Yellow: #FFDF00 (Bandeira amarela)
 * - White: #FFFFFF
 */

export interface Theme {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    controlBg: string;
    controlText: string;
    error: string;
    success: string;
    overlay: string;
  };
  fonts: {
    family: string;
    sizeSmall: string;
    sizeMedium: string;
    sizeLarge: string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  borderRadius: string;
  transition: string;
}

export const darkTheme: Theme = {
  colors: {
    primary: '#006B2B',
    secondary: '#002776',
    accent: '#FFDF00',
    background: '#0a0a0a',
    surface: '#1a1a2e',
    text: '#ffffff',
    textSecondary: '#b0b0b0',
    controlBg: 'rgba(0, 0, 0, 0.7)',
    controlText: '#ffffff',
    error: '#ff4444',
    success: '#006B2B',
    overlay: 'rgba(0, 0, 0, 0.85)',
  },
  fonts: {
    family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    sizeSmall: '0.75rem',
    sizeMedium: '0.875rem',
    sizeLarge: '1.125rem',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
  },
  borderRadius: '8px',
  transition: '0.2s ease',
};

export const lightTheme: Theme = {
  colors: {
    primary: '#006B2B',
    secondary: '#002776',
    accent: '#FFDF00',
    background: '#f5f5f5',
    surface: '#ffffff',
    text: '#1a1a1a',
    textSecondary: '#666666',
    controlBg: 'rgba(255, 255, 255, 0.9)',
    controlText: '#1a1a1a',
    error: '#cc0000',
    success: '#006B2B',
    overlay: 'rgba(255, 255, 255, 0.9)',
  },
  fonts: {
    family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    sizeSmall: '0.75rem',
    sizeMedium: '0.875rem',
    sizeLarge: '1.125rem',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
  },
  borderRadius: '8px',
  transition: '0.2s ease',
};

/**
 * Detect OS theme preference and return the appropriate theme.
 */
export function getSystemTheme(): 'dark' | 'light' {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/**
 * Resolve theme based on config preference.
 */
export function resolveTheme(preference: 'light' | 'dark' | 'auto'): Theme {
  if (preference === 'auto') {
    return getSystemTheme() === 'dark' ? darkTheme : lightTheme;
  }
  return preference === 'dark' ? darkTheme : lightTheme;
}
